<template>
  <div class="editor-page simple-mode">
    <FeatureAlertCard
      v-if="account && !account.mailTemplate"
      class="mb-4"
    >
      <template v-slot:title>
        {{ $t('mailTemplateNotAvailable.title') }}
      </template>
    </FeatureAlertCard>
    <div class="editor-description">
      <p>{{ $t('editor.intro') }}</p>
      <ul>
        <li>
          <b>{{ $t('editor.visualEditor.title') }}</b> — {{ $t('editor.visualEditor.text') }}
        </li>
        <li>
          <b @click="showCodeEditor" class="link-editor-code">{{ $t('editor.codeEditor.title') }}</b>
          — {{ $t('editor.codeEditor.text') }}
        </li>
      </ul>
    </div>

    <MailTemplateEditor
      v-if="account && company"
      :company="company"
      :account="account"
      :is-show-code-editor="isShowCodeEditor"
      @closeCodeEditor="closeCodeEditor"
    />

    <Preloader v-else />
  </div>
</template>

<script>
import { ref, onMounted } from '@vue/composition-api';
import useAccount from '@/compositions/accounting/useAccount';
import MailTemplateEditor from '@/components/NotificationTemplates/MailTemplateEditor.vue';
import FeatureAlertCard from '@/components/Company/FeatureAlertCard.vue';
import Preloader from '../Preloader.vue';

export default {
  name: 'MailTemplateEditorTab',
  components: { MailTemplateEditor, FeatureAlertCard, Preloader },
  props: {
    company: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { fetchIfNull, account } = useAccount();
    const isShowCodeEditor = ref(false);

    const showCodeEditor = () => {
      isShowCodeEditor.value = true;
    };

    const closeCodeEditor = () => {
      isShowCodeEditor.value = false;
    };

    onMounted(async () => {
      await fetchIfNull();
    });

    return {
      showCodeEditor,
      closeCodeEditor,
      isShowCodeEditor,
      account,
    };
  },
};
</script>

<style lang="scss" scoped>
.editor-description {
  margin-bottom: 30px;
}

.link-editor-code {
  cursor: pointer;
  text-decoration: underline;
}
</style>

<i18n locale="en" lang="yaml">
mailTemplateNotAvailable:
  title: The career portal is available on the Standard plan and above.
editor:
  intro: 'In this section, you can customize the email style. There are two available options:'
  codeWarning: "You can edit <b>only the email code</b> here. The block <code>SELECTED BLOCK</code> must always remain in place for correct operation."
  visualEditor:
    title: Visual Editor
    text: a convenient tool that allows you to add a logo, change fonts, choose a background, and insert necessary elements without working with code.
  codeEditor:
    title: Code Editor
    text: enables you to manually edit the email's HTML code and adjust its appearance to meet your specific requirements.
</i18n>

<i18n locale="ru" lang="yaml">
mailTemplateNotAvailable:
  title: Редактор письма доступен на тарифе «Стандарт» и выше.
editor:
  intro: 'В этом разделе вы можете настроить стиль письма. Для этого доступны два способа:'
  codeWarning: "Здесь можно редактировать <b>только код письма</b>. Блок <code>ВЫДЕЛЕННЫЙ БЛОК</code> всегда должен оставаться на месте для корректной работы."
  visualEditor:
    title: Визуальный редактор
    text: удобный инструмент, с помощью которого можно добавить логотип, изменить шрифт, выбрать фон и вставить необходимые элементы без необходимости работать с кодом.
  codeEditor:
    title: Редактор кода
    text: позволяет вручную внести изменения в HTML-код письма и настроить его внешний вид в соответствии с вашими требованиями.
</i18n>
